import { useEffect, useState } from 'react';
import { X, Smartphone, Share, PlusSquare } from 'lucide-react';
import { Modal } from './ui/Modal';
import { usePwaInstall } from '../hooks/usePwaInstall';

const DISMISS_KEY = 'orion_pwa_install_prompt_dismissed';

export function InstallAppModal() {
  const { canPromptInstall, isInstalled, isIos, promptInstall } = usePwaInstall();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isInstalled) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Só oferece o popup quando há como instalar de fato (Android/desktop com
    // beforeinstallprompt, ou iOS via instruções de "Adicionar à Tela de Início").
    if (!canPromptInstall && !isIos) return;

    const timer = setTimeout(() => setIsOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [canPromptInstall, isIos, isInstalled]);

  const dismiss = (persist: boolean) => {
    setIsOpen(false);
    if (persist) {
      try {
        localStorage.setItem(DISMISS_KEY, '1');
      } catch {
        // localStorage indisponível (modo privado, etc.) - apenas fecha o popup.
      }
    }
  };

  const handleInstallClick = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted' || outcome === 'dismissed') {
      dismiss(true);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={() => dismiss(false)} cardClassName="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full shadow-2xl relative" zIndexClassName="z-[60]">
      <button
        onClick={() => dismiss(false)}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        aria-label="Fechar"
      >
        <X size={20} />
      </button>

      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
          <Smartphone className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Instale o Orion CRM no seu celular
        </h2>

        {isIos ? (
          <div className="text-gray-600 dark:text-gray-300 mb-6 text-sm text-left w-full space-y-2 bg-gray-50 dark:bg-slate-700/40 rounded-lg p-4">
            <p className="text-center mb-2">Adicione o app à sua tela de início em 2 passos:</p>
            <p className="flex items-center gap-2">
              <Share size={16} className="shrink-0" /> Toque em <strong>Compartilhar</strong> na barra do Safari
            </p>
            <p className="flex items-center gap-2">
              <PlusSquare size={16} className="shrink-0" /> Toque em <strong>Adicionar à Tela de Início</strong>
            </p>
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
            Acesse mais rápido, com tela cheia e notificações, direto do ícone na tela inicial do seu Android ou computador.
          </p>
        )}

        {isIos ? (
          <button
            onClick={() => dismiss(true)}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-lg"
          >
            Entendi
          </button>
        ) : (
          <button
            onClick={handleInstallClick}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-lg"
          >
            Instalar agora
          </button>
        )}

        <button
          onClick={() => dismiss(true)}
          className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Agora não
        </button>
      </div>
    </Modal>
  );
}
